/* eslint-disable array-bracket-newline */
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ITicker, enums } from '@kattana-io/common';
import eventEmitter from '../../../core/eventEmitter';
import {
  getLastCexPriceEventName,
  getLastTradeEventName,
} from '../../../helpers/events-builder';
import { subscription } from '../../../store/createStore';
import { IStore } from '../../../reducers/index';
import { getPrice } from '../../Modal/TickersModal/helpers';
import useTicker from '../../../hooks/useTicker';
import { ILastTrade } from '../../../core/BlocksSubscription';

export type LivePrice = {
  rawPrice: number;
  formattedPrice: string;
  sign: string;
};

export interface ITickerLivePrice {
  price: LivePrice;
  side: 'buy' | 'sell' | undefined;
}

const getBasePrice = (ticker: ITicker) => {
  const { price, price_usd, quoteIsStablecoin } = useTicker(ticker);
  if (price_usd) {
    return {
      rawPrice: price_usd,
      formattedPrice: `$${price_usd.toFixed(2)}`,
      sign: '$',
    };
  }
  if (quoteIsStablecoin) {
    return {
      rawPrice: price_usd,
      formattedPrice: `$${price.toFixed(2)}`,
      sign: '$',
    };
  } // if quote is USD                         // 1 WETH = ... KTN (not USD price)
  return { rawPrice: 0, formattedPrice: '', sign: '' }; // do not show if we don't have any price
};

const useTickerLivePrice = (ticker: ITicker): ITickerLivePrice => {
  const [price, setPrice] = useState(getBasePrice(ticker));
  const [side, setSide] = useState<'buy' | 'sell'>();
  const rates = useSelector((store: IStore) => store.rates.items);

  useEffect(() => {
    setPrice(getBasePrice(ticker));
    setSide(undefined);
    let eventName: string;
    const dexLastPriceChangeHandler = ({ trade }: { trade: ILastTrade }) => {
      if (trade) {
        if (ticker.flipped && trade.price_b_usd != 0) {
          setSide(trade.is_buy ? 'sell' : 'buy');
          setPrice({
            rawPrice: trade.price_b_usd,
            formattedPrice: `$${trade.price_b_usd.toFixed(2)}`,
            sign: '$',
          });
        } else if (!ticker.flipped && trade.price_a_usd != 0) {
          setSide(trade.is_buy ? 'buy' : 'sell');
          setPrice({
            rawPrice: trade.price_a_usd,
            formattedPrice: `$${trade.price_a_usd.toFixed(2)}`,
            sign: '$',
          });
        } else {
          const priceUSD = getPrice(ticker, rates);
          setPrice(priceUSD);
        }
      }
    };

    const cexLastPriceChangeHandler = (evdata: { price: number }) => {
      if (evdata) {
        if (evdata.price != 0) {
          setPrice((prevData) => {
            setSide(prevData.rawPrice > evdata.price ? 'sell' : 'buy');
            return {
              rawPrice: evdata.price,
              formattedPrice: `$${evdata.price.toFixed(2)}`,
              sign: '$',
            };
          });
        } else {
          const priceUSD = getPrice(ticker, rates);
          setPrice(priceUSD);
        }
      }
    };

    if (ticker.kind === enums.ETickerKind.DEX) {
      eventName = getLastTradeEventName(ticker.chain, ticker.address);
      subscription.subscribeToLastTrade(eventName, ticker);
      eventEmitter.on(eventName, dexLastPriceChangeHandler);
    } else if (ticker.kind === enums.ETickerKind.CEX) {
      eventName = getLastCexPriceEventName(ticker.exchange, ticker.id);
      subscription.subscribeToLastCEXPrice(
        eventName,
        ticker.exchange,
        ticker.id
      );
      eventEmitter.on(eventName, cexLastPriceChangeHandler);
    }

    return () => {
      if (ticker.kind === enums.ETickerKind.DEX) {
        eventEmitter.removeListener(eventName, dexLastPriceChangeHandler);
        subscription.unsubscribeFromLastTrade(eventName);
      } else if (ticker.kind === enums.ETickerKind.CEX) {
        eventEmitter.removeListener(eventName, cexLastPriceChangeHandler);
        subscription.unsubscribeFromLastCexPrice(eventName);
      }
    };
  }, [ticker, rates]);

  const data = useMemo(() => ({ price, side }), [price, side]);

  return data;
};

export default useTickerLivePrice;
